import React, { useEffect, useState } from 'react';
import { Edit2, Trash2, Key, Power, PowerOff, CheckCircle2, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { AdminService } from '../../services/api';
import ResetPasswordModal from '../ResetPasswordModal';

const ClinicDetails = ({ selectedClinic, linkedDoctors, linkedPharmacies, onDoctorClick, onPharmacyClick, onBack, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const userRole = Cookies.get('role');
  const [currentStatus, setCurrentStatus] = useState(selectedClinic.vendor_approve_status || 'A');
  const [statusLoading, setStatusLoading] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const [planAllocation, setPlanAllocation] = useState(null);

  useEffect(() => {
    AdminService.getVendorModuleSubscriptions(selectedClinic.id)
      .then((response) => setPlanAllocation(response?.status === 1 ? (response.data?.[0] || null) : null))
      .catch(() => setPlanAllocation(null));
  }, [selectedClinic.id]);

  const isActive = currentStatus === 'A';
  const commercialModel = planAllocation?.commercial_model || (selectedClinic.billing_model === 'commission' ? 'commission_only' : 'subscription_only');
  const isCommissionOnly = commercialModel === 'commission_only';
  const formatDate = (value) => value ? String(value).slice(0, 10) : 'Not Set';

  const handleToggleStatus = async () => {
    const nextStatus = isActive ? 'D' : 'A';
    const actionLabel = isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${actionLabel} this clinic?`)) return;

    setStatusLoading(true);
    try {
      const response = await AdminService.setVendorStatus(selectedClinic.id, nextStatus);
      if (response && response.status === 1) {
        setCurrentStatus(nextStatus);
        selectedClinic.vendor_approve_status = nextStatus;
        setAlertMsg(`Clinic ${isActive ? 'deactivated' : 'activated'} successfully!`);
        setTimeout(() => setAlertMsg(''), 4000);
      } else {
        alert(response?.msg || 'Failed to update clinic status');
      }
    } catch (err) {
      alert(err.message || 'Error updating status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDoctorClick = (doctor) => {
    navigate(`/doctor-appointments/${doctor.id}/${selectedClinic.id}`);
  };

  const handlePharmacyClick = (pharmacy) => {
    navigate(`/pharmacy-appointments/${pharmacy.id}`);
  };

  return (
    <div className="detail-view">
      {alertMsg && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold flex items-center gap-2">
          <CheckCircle2 size={16} />
          <span>{alertMsg}</span>
        </div>
      )}
      <div className="detail-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Clinics
        </button>
        <div className="flex items-center gap-3">
          <h2>{selectedClinic.vendor_name || selectedClinic.vendorName || 'Unnamed Clinic'}</h2>
          <span className={`status-pill ${isActive ? 'approved' : 'pending'}`}>
            <span className="dot" />
            {isActive ? 'Approved / Active' : 'Deactivated'}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {userRole === 'SuperAdmin' && (
            <>
              <button
                className={`create-btn flex items-center gap-1 ${
                  isActive ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
                onClick={handleToggleStatus}
                disabled={statusLoading}
              >
                {isActive ? <PowerOff size={16} /> : <Power size={16} />}
                {statusLoading ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button
                className="create-btn flex items-center gap-1 bg-slate-700 hover:bg-slate-800"
                onClick={() => setIsResetModalOpen(true)}
              >
                <Key size={16} /> Reset Password
              </button>
              <button className="create-btn flex items-center gap-1" onClick={onEdit}>
                <Edit2 size={16} /> Edit
              </button>
              <button className="create-btn flex items-center gap-1 bg-red-500 hover:bg-red-600" onClick={onDelete}>
                <Trash2 size={16} /> Delete
              </button>
            </>
          )}
        </div>
      </div>
      <ResetPasswordModal
        vendor={selectedClinic}
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        onSuccess={() => setAlertMsg('Password reset successfully')}
      />
      <div className="detail-content">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Profile Photo:</span>
            {selectedClinic.vendor_profile_path ? (
              <img
                src={selectedClinic.vendor_profile_path}
                alt={selectedClinic.vendor_name || 'Clinic Image'}
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <span>No Image</span>
            )}
          </div>
          <div className="info-item">
            <span className="info-label">Email:</span>
            <span>{selectedClinic.vendor_email || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Mobile Number:</span>
            <span>{selectedClinic.vendor_phone || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Location:</span>
            <span>{selectedClinic.vendor_location || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Coordinates (Lat, Long):</span>
            <span>{selectedClinic.vendor_latlong || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Registration Number:</span>
            <span>{selectedClinic.reg_no || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Address:</span>
            <span>{selectedClinic.vendor_address || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Medical License Expiry:</span>
            <span>
              {selectedClinic.exp_date
                ? new Date(selectedClinic.exp_date).toLocaleDateString('en-CA', { timeZone: 'UTC' })
                : 'N/A'}
            </span>
          </div>
        </div>

        {/* Billing & Subscription Details */}
        <div className="mb-6 p-5 rounded-2xl border border-slate-100 bg-slate-50">
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
            <CreditCard size={16} className="text-teal-600" />
            Billing &amp; Subscription Configuration
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-3 rounded-xl border border-slate-200">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Billing Model</span>
              <span className={`inline-block text-xs font-bold px-2.5 py-0.5 rounded-full ${
                isCommissionOnly
                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                  : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`}>
                {isCommissionOnly ? 'Commission Only' : 'Subscription Only'}
              </span>
            </div>

            {isCommissionOnly ? (
              <>
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Plan</span>
                  <span className="text-sm font-bold text-slate-800">{planAllocation?.plan_name || 'Legacy Commission Plan'}</span>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Commission Rate</span>
                  <span className="text-sm font-bold text-slate-800">
                    {planAllocation?.commission_rate ?? selectedClinic.commission_rate ?? '0'} {(planAllocation?.commission_type || selectedClinic.commission_type) === 'percentage' ? '%' : `${planAllocation?.currency || 'INR'} per booking`}
                  </span>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Commission Type</span>
                  <span className="text-sm font-bold text-slate-800 capitalize">
                    {planAllocation?.commission_type || selectedClinic.commission_type || 'Percentage'}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Plan</span>
                  <span className="text-sm font-bold text-slate-800">{planAllocation?.plan_name || 'Legacy Subscription Plan'}</span>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Subscription Fee</span>
                  <span className="text-sm font-bold text-teal-700">
                    {planAllocation?.currency || 'INR'} {Number(planAllocation?.subscription_amount ?? selectedClinic.subscription_amount ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Start Date</span>
                  <span className="text-sm font-semibold text-slate-700">
                    {formatDate(planAllocation?.billing_start_date || selectedClinic.billing_start_date)}
                  </span>
                </div>
                <div className="bg-white p-3 rounded-xl border border-slate-200">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">End Date</span>
                  <span className="text-sm font-semibold text-slate-700">
                    {formatDate(planAllocation?.billing_end_date || selectedClinic.billing_end_date)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="linked-sections">
          <div className="linked-section">
            <h3>Linked Doctors ({linkedDoctors.length})</h3>
            <div className="linked-cards">
              {linkedDoctors.map((doctor) => (
                <div key={doctor.id} className="linked-card" onClick={() => handleDoctorClick(doctor)}>
                  <h4>{doctor.vendor_name || doctor.vendorName || 'Unnamed Doctor'}</h4>
                  <p>{doctor.specialty || 'N/A'}</p>
                  <span className={`status ${(doctor.vendor_approve_status || '').toLowerCase()}`}>
                    {doctor.vendor_approve_status === 'A' ? 'Approved' : doctor.vendor_approve_status || 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="linked-section">
            <h3>Linked Pharmacies ({linkedPharmacies.length})</h3>
            <div className="linked-cards">
              {linkedPharmacies.map((pharmacy) => (
                <div key={pharmacy.id} className="linked-card" onClick={() => handlePharmacyClick(pharmacy)}>
                  <h4>{pharmacy.vendor_name || pharmacy.vendorName || 'Unnamed Pharmacy'}</h4>
                  <p>{pharmacy.location || 'N/A'}</p>
                  <span className={`status ${(pharmacy.vendor_approve_status || '').toLowerCase()}`}>
                    {pharmacy.vendor_approve_status === 'A' ? 'Approved' : pharmacy.vendor_approve_status || 'N/A'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClinicDetails;
